import type { OnInit } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  Inject,
  Input,
} from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Location } from '@freelancer/location';
import { ButtonColor } from '@freelancer/ui/button';
import { CalloutColor, CalloutPlacement } from '@freelancer/ui/callout';
import { ContainerSize } from '@freelancer/ui/container';
import { IconColor, IconSize } from '@freelancer/ui/icon';
import { LinkColor, LinkUnderline } from '@freelancer/ui/link';
import { BackgroundColor, LogoSize } from '@freelancer/ui/logo';
import { ModalService, ModalSize } from '@freelancer/ui/modal';
import { StickyBehaviour } from '@freelancer/ui/sticky';
import { TextSize } from '@freelancer/ui/text';
import { UI_CONFIG } from '@freelancer/ui/ui.config';
import { UiConfig } from '@freelancer/ui/ui.interface';
import type { LoggedOutShellConfig } from 'app/app-shell';
import { ShellConfig } from 'app/app-shell';
import type { Observable } from 'rxjs';
import { filter, map, startWith } from 'rxjs/operators';
import { LoginOrSignup } from '../../login-signup/login-signup-modal';
import { CtaButtonType, NavigationAlternate } from '../navigation.model';
import { LOGGED_OUT_NAV_HEIGHT } from './navigation-logged-out-home.types';

@Component({
  standalone: false,
  selector: 'app-navigation-logged-out-home',
  template: `
    <div
      class="NavigationRoot"
      flTrackingSection="NavigationLoggedOut"
      [flSticky]="!(config$ | flAsync)?.disableStickyNavBar"
      [flStickyBehaviour]="StickyBehaviour.ALWAYS"
    >
      <fl-container
        class="NavigationContainer"
        [mobilePadding]="true"
        [sizeDesktopXLarge]="containerSizeDesktopXLarge"
        [size]="size"
      >
        <fl-button
          class="LogoButton"
          link="/"
          display="block"
          flTrackingLabel="Logo"
          [attr.data-long]="
            !!alternateNavigation &&
            !!alternateNavigation.logo.mobileDefault &&
            !alternateNavigation.logo.tabletDefault
          "
          [attr.data-long-desktop]="
            !!alternateNavigation &&
            !!alternateNavigation.logo.mobileDefault &&
            !!alternateNavigation.logo.tabletDefault
          "
        >
          @if (!alternateNavigation) {
            <fl-logo
              [backgroundColor]="BackgroundColor.DARK"
              [size]="LogoSize.SMALL"
            ></fl-logo>
          }
          @if (alternateNavigation) {
            @if (alternateNavigation) {
              <fl-picture
                [src]="alternateNavigation.logo.transparent"
                [class.mobile:hide]="!!alternateNavigation.logo.mobileDefault"
                [class.tablet:hide]="!!alternateNavigation.logo.tabletDefault"
              ></fl-picture>
            }
            @if (alternateNavigation.logo.mobileDefault) {
              <fl-picture
                class="desktop:hide tablet:hide"
                [src]="alternateNavigation.logo.mobileTransparent"
              ></fl-picture>
            }
            @if (alternateNavigation.logo.tabletDefault) {
              <fl-picture
                class="desktop:hide mobile:hide"
                [src]="alternateNavigation.logo.tabletTransparent"
              ></fl-picture>
            }
          }
        </fl-button>
        @if (!hideLinks) {
          <app-navigation-logged-out-home-section-hire-freelancers>
          </app-navigation-logged-out-home-section-hire-freelancers>
          <app-navigation-logged-out-home-section-find-work>
          </app-navigation-logged-out-home-section-find-work>
          <app-navigation-logged-out-home-section-solutions>
          </app-navigation-logged-out-home-section-solutions>
        }
        <div class="NavigationSections">
          @if (!loginSignupModal) {
            <fl-link
              class="NavigationItem mr-small"
              flTrackingLabel="LoginPage"
              i18n="Navigation log in label"
              [link]="'/login'"
              [queryParams]="{ next: redirectUrl$ | flAsync }"
              [size]="TextSize.SMALL"
              [color]="LinkColor.LIGHT"
              [underline]="LinkUnderline.NEVER"
            >
              Log In
            </fl-link>
          }
          @if (loginSignupModal) {
            <fl-link
              class="NavigationItem mr-small"
              flTrackingLabel="LoginModal"
              i18n="Navigation log in label"
              [size]="TextSize.LARGE"
              [color]="LinkColor.LIGHT"
              [underline]="LinkUnderline.NEVER"
              (click)="handleLoginSignupModal(LoginOrSignup.LOGIN)"
            >
              Log In
            </fl-link>
          }
          @if (!loginSignupModal) {
            <fl-link
              class="NavigationItem tablet:mr-small mr-small"
              flTrackingLabel="SignUp"
              i18n="Navigation sign up label"
              [size]="TextSize.SMALL"
              [link]="'/signup'"
              [queryParams]="{ next: redirectUrl$ | flAsync }"
              [color]="LinkColor.LIGHT"
              [underline]="LinkUnderline.NEVER"
            >
              Sign Up
            </fl-link>
          }
          @if (loginSignupModal) {
            <fl-link
              class="NavigationItem {{
                disablePostProjectButton ? 'tablet:mr-none' : 'tablet:mr-small'
              }}"
              flTrackingLabel="SignUp"
              i18n="Navigation sign up label"
              [size]="TextSize.LARGE"
              [color]="LinkColor.LIGHT"
              [underline]="LinkUnderline.NEVER"
              (click)="handleLoginSignupModal(LoginOrSignup.SIGNUP)"
            >
              Sign Up
            </fl-link>
          }
          @if (!disablePostProjectButton) {
            @if (!alternateNavigation) {
              <div class="NavigationItem mobile:hide tablet:hide">
                <fl-button
                  i18n="Navigation post project button"
                  flTrackingLabel="PostProjectButton"
                  [color]="ButtonColor.PRIMARY"
                  [link]="'/post-project'"
                >
                  Post a Project
                </fl-button>
              </div>
            }
            @if (alternateNavigation && alternateNavigation.ctaLink) {
              <div class="NavigationItem mobile:hide">
                <ng-container [ngSwitch]="alternateNavigation.ctaButtonType">
                  <fl-button
                    *ngSwitchCase="CtaButtonType.DEMO"
                    i18n="Navigation request enterprise demo button"
                    flTrackingLabel="RequestDemoButton"
                    [color]="ButtonColor.PRIMARY"
                    [link]="alternateNavigation.ctaLink"
                  >
                    Request a Demo
                  </fl-button>
                  <fl-button
                    *ngSwitchCase="CtaButtonType.POST_PROJECT"
                    i18n="Navigation enterprise post a project button"
                    flTrackingLabel="EnterprisePostProjectButton"
                    [color]="ButtonColor.PRIMARY"
                    [link]="alternateNavigation.ctaLink"
                  >
                    Post a Project
                  </fl-button>
                </ng-container>
              </div>
            }
          }
          <fl-callout
            class="desktop:hide"
            [hideCloseButton]="true"
            [color]="CalloutColor.DARK"
            [placement]="CalloutPlacement.RIGHT"
            [mobileFullscreen]="true"
            (calloutOpen)="menuRedirect()"
          >
            <fl-callout-trigger>
              <div class="MenuContainer">
                <fl-icon
                  i18n-title="Menu icon title"
                  title="Menu"
                  [color]="IconColor.LIGHT"
                  [name]="'ui-bars-v2'"
                  [useIconFont]="true"
                  [size]="IconSize.SMALL"
                ></fl-icon>
              </div>
            </fl-callout-trigger>
            <fl-callout-content>
              <app-navigation-logged-out-home-menu></app-navigation-logged-out-home-menu>
            </fl-callout-content>
          </fl-callout>
        </div>
      </fl-container>
    </div>
  `,
  styleUrls: ['./navigation-logged-out-home.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NavigationLoggedOutHomeComponent implements OnInit {
  BackgroundColor = BackgroundColor;
  ButtonColor = ButtonColor;
  CalloutPlacement = CalloutPlacement;
  CalloutColor = CalloutColor;
  CtaButtonType = CtaButtonType;
  IconColor = IconColor;
  IconSize = IconSize;
  TextSize = TextSize;
  LinkColor = LinkColor;
  LinkUnderline = LinkUnderline;
  LoginOrSignup = LoginOrSignup;
  LogoSize = LogoSize;
  StickyBehaviour = StickyBehaviour;

  hideLinks = false;
  loginSignupModal: boolean;
  size: ContainerSize;
  sizeDesktopXLarge: ContainerSize;
  alternateNavigation: NavigationAlternate;

  selectedTab?: boolean;

  @Input() set containerSize(value: ContainerSize) {
    this.size = value || ContainerSize.DESKTOP_LARGE;
  }

  @Input() containerSizeDesktopXLarge?: ContainerSize;

  @Input() set useLoginSignupModal(value: boolean) {
    this.loginSignupModal = value || false;
  }

  @Input() set navigationAlternate(value: NavigationAlternate) {
    this.alternateNavigation = value || false;
  }

  @Input() disablePostProjectButton = false;

  @HostBinding('style.--logged-out-nav-height')
  loggedOutNavHeight = LOGGED_OUT_NAV_HEIGHT;

  redirectUrl$: Observable<string>;
  config$: Observable<LoggedOutShellConfig>;

  constructor(
    @Inject(UI_CONFIG) private uiConfig: UiConfig,
    private activatedRoute: ActivatedRoute,
    private location: Location,
    private modalService: ModalService,
    private router: Router,
    private shellConfig: ShellConfig,
  ) {}

  ngOnInit(): void {
    this.config$ = this.shellConfig.getConfig(this.activatedRoute);

    this.redirectUrl$ = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      map(() => this.location.url),
      startWith(this.location.url),
      filter(url => new URL(`${this.location.origin}${url}`).pathname !== '/'),
    );
    this.hideLinks = this.uiConfig.theme === 'freightlancer';
  }

  handleLoginSignupModal(loginOrSignup: LoginOrSignup): void {
    if (this.loginSignupModal) {
      this.modalService.open('LoginSignupModal', {
        inputs: {
          form: loginOrSignup,
        },
        size: ModalSize.XSMALL,
        edgeToEdge: true,
        closeable: true,
        mobileFullscreen: true,
      });
    }
  }

  menuRedirect(): void {
    this.router.navigate(['/logged-out-menu']);
  }
}
